import { useCallback, useEffect, useState } from "react";
import { DownloadSimple, ArrowClockwise, Flag } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { api, registerError, qty, dayLabel } from "./registerApi";
import { CountTrueUp } from "./CountTrueUp";

const STATUS_STYLES = {
  closed: "bg-[#ECFDF3] text-[#027A48] border-[#A6F4C5]",
  open: "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]",
  pending: "bg-[#F9FAFB] text-[#475467] border-[#D0D5DD]",
};

// The desk view: items down, days across, with handovers, declared total,
// counted closing, actual consumption and variance - the same dense table
// language as the production and BOM screens.
export const WeekReviewGrid = ({ processId, refreshKey }) => {
  const [weeks, setWeeks] = useState([]);
  const [weekId, setWeekId] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (id) => {
    setLoading(true);
    try {
      const [list, data] = await Promise.all([api.weeks(processId), api.review(processId, id)]);
      setWeeks(list);
      setReview(data);
      setWeekId(data.week.id);
    } catch (err) {
      toast.error(registerError(err).message);
    } finally {
      setLoading(false);
    }
  }, [processId]);

  useEffect(() => { load(weekId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [processId, refreshKey]);

  if (loading && !review) return <div className="p-6 text-center text-sm text-[#667085]">Loading the week...</div>;
  if (!review) return null;

  const { week, rows, days } = review;

  return (
    <div className="space-y-3" data-testid="week-review">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={weekId || ""} onValueChange={(v) => load(v)}>
            <SelectTrigger className="h-9 w-[220px]" data-testid="week-picker">
              <SelectValue placeholder="Week" />
            </SelectTrigger>
            <SelectContent>
              {weeks.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  Week {w.week_no} · {w.start_date || "not started"} · {w.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className={`text-xs font-bold uppercase tracking-wider border rounded-sm px-2 py-1 ${STATUS_STYLES[week.status]}`} data-testid="week-status-badge">
            {week.status}{week.day_index ? ` · day ${week.day_index}` : ""}
          </span>
          {review.flagged_handovers > 0 && (
            <span className="text-xs font-bold border rounded-sm px-2 py-1 bg-[#FFFAEB] text-[#B54708] border-[#FEDF89] flex items-center gap-1" data-testid="flagged-handovers-badge">
              <Flag size={12} weight="bold" /> {review.flagged_handovers} flagged handover(s)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => load(weekId)} data-testid="review-refresh">
            <ArrowClockwise size={14} weight="bold" /> Refresh
          </Button>
          <Button size="sm" variant="outline" asChild data-testid="review-export">
            <a href={api.exportUrl(processId, weekId)}>
              <DownloadSimple size={14} weight="bold" /> Download sheet
            </a>
          </Button>
        </div>
      </div>

      <div className="text-xs text-[#667085]">
        {review.department.name} · {review.process.name} · site {review.department.site_id} · cost centre{" "}
        <span className="font-mono font-bold text-[#344054]">{review.department.cost_center_id}</span>
        {week.closed_at && <> · closed by {week.closed_by}</>}
        {week.opening_source === "godown_match" && (
          <> · <span className="text-[#027A48] font-semibold" data-testid="week-opening-matched">opening matched to the godown</span></>
        )}
      </div>

      <CountTrueUp weekId={weekId} />

      <div className="overflow-auto border border-[#D0D5DD] rounded-sm bg-white">
        <table className="w-full text-[13px] border-collapse" data-testid="week-grid">
          <thead className="bg-[#F9FAFB]">
            <tr>
              {["Item", "Unit", "Opening", "Brought in", ...days.map(dayLabel), "Declared", "Counted", "Actual", "Variance", "Var %", "Reason"].map((h, i) => (
                <th key={i} className="border border-[#D0D5DD] px-2 py-1.5 text-left font-heading text-[11px] font-bold uppercase tracking-wider text-[#475467] whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={11 + days.length} className="text-center py-8 text-[#667085]">Nothing recorded in this week yet.</td></tr>
            )}
            {rows.map((row, i) => (
              <tr key={row.product_id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`review-row-${row.product_id}`}>
                <td className="border border-[#D0D5DD] px-2 py-1.5">
                  <div className="font-mono font-bold text-[#101828] whitespace-nowrap">{row.product_id}</div>
                  <div className="text-[11px] text-[#667085] max-w-[220px] truncate">{row.description || "—"}</div>
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467] whitespace-nowrap">{row.unit_code}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{qty(row.opening)}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{qty(row.handovers)}</td>
                {days.map((d) => (
                  <td key={d} className={`border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums ${row.by_day[d] == null ? "bg-[#F2F4F7] text-[#98A2B3]" : ""}`}>
                    {row.by_day[d] == null ? "—" : qty(row.by_day[d])}
                  </td>
                ))}
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums font-bold">{qty(row.declared)}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{qty(row.counted)}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums font-bold">{qty(row.actual)}</td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{qty(row.variance)}</td>
                <td className={`border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums font-bold ${row.out_of_tolerance ? "text-[#B42318]" : "text-[#475467]"}`}>
                  {row.variance_pct == null ? "—" : `${row.variance_pct}%`}
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467] max-w-[200px]">{row.reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {review.handovers.length > 0 && (
        <div className="border border-[#D0D5DD] rounded-sm bg-white overflow-auto">
          <div className="px-2 py-1.5 bg-[#F9FAFB] border-b border-[#D0D5DD] font-heading text-[11px] font-bold uppercase tracking-wider text-[#475467]">
            Handovers claimed this week
          </div>
          <table className="w-full text-[13px] border-collapse">
            <tbody>
              {review.handovers.map((h, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"}>
                  <td className="px-2 py-1.5 font-mono whitespace-nowrap">{h.product_id}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold">{qty(h.quantity)} {h.unit_code}</td>
                  <td className="px-2 py-1.5 text-[#475467] whitespace-nowrap">{h.actor}</td>
                  <td className="px-2 py-1.5">
                    {h.over_claim && (
                      <span className="text-[11px] font-bold text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-1.5 py-0.5">
                        more than SAP showed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
